import { useContext, useRef } from 'react';

import './VendorLeadStepper.scss';

import { cNs } from 'src/helpers';
import { isVendorStepStarted } from 'src/helpers/isVendorStepStarted';
import { isVendorStepSubmitted } from 'src/helpers/isVendorStepSubmitted';
import { useDebounceEffect } from 'src/hooks/useDebounceEffect';
import { VendorsContext } from 'src/pages/VendorsPage/VendorsPage.context';

type VendorLeadStepperProps = {
  isStartPage?: boolean;
};

export function VendorLeadStepper({ isStartPage }: Readonly<VendorLeadStepperProps>) {
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const currentStepRef = useRef<HTMLDivElement>(null);

  const { isEditing, vendorStep, vendorStepsInfo, vendorLeadProduct, vendorLeadProductSpecification, setVendorStep } =
    useContext(VendorsContext);

  useDebounceEffect(
    () => {
      const currentStepPosition = currentStepRef.current?.getBoundingClientRect().left;

      if (currentStepPosition) {
        stepContainerRef.current?.scroll({
          left: currentStepPosition - 16, // - padding
          behavior: 'smooth',
        });
      }
    },
    [vendorStep],
    100
  );

  return (
    <div
      className={cNs('vendor-lead-stepper__container', isStartPage && 'vendor-lead-stepper__container__start-page')}
      ref={stepContainerRef}
    >
      {vendorStepsInfo.map((stepInfo, index) => {
        const isSelected = vendorStep === stepInfo.step;

        const isStarted = isVendorStepStarted(stepInfo.step, vendorLeadProduct, vendorLeadProductSpecification);
        const isSubmitted = isVendorStepSubmitted(stepInfo.step, vendorLeadProduct, vendorLeadProductSpecification);
        const isLast = index === vendorStepsInfo.length - 1;

        const handleClick =
          !isEditing && !isSelected
            ? () => {
                setVendorStep(stepInfo.step);
              }
            : undefined;

        return (
          <div
            key={stepInfo.step}
            ref={isSelected ? currentStepRef : undefined}
            className={cNs(
              'vendor-lead-stepper__step',
              isSelected && 'vendor-lead-stepper__step-selected',
              isEditing && !isSelected && 'vendor-lead-stepper__step-disabled',
              isSubmitted
                ? 'vendor-lead-stepper__step-submitted'
                : isStarted
                  ? 'vendor-lead-stepper__step-started'
                  : null
            )}
            onClick={handleClick}
          >
            {!isLast && <div className="vendor-lead-stepper__step-arrow" />}
            {stepInfo.icon}
            <div className="flex column nowrap">
              <div className="vendor-lead-stepper__step-number">Step {index + 1}</div>
              <div className="vendor-lead-stepper__step-title">{stepInfo.description}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
